"use strict";

// ---------------------------------------------------
// Load Super Class

var SoaModel = require('bunsen/models/SoaModel');

// ---------------------------------------------------
// Module Definition

/**
 * @class Review
 * @extends SoaModel
 */
var Review = module.exports = SoaModel.extend();

// ---------------------------------------------------
// Module Constants

var assert = require('bunsen/helpers/assert');

var REVIEW_STATUSES = {
    NEW: 'NEW',
    PENDING: 'PENDING',
    APPROVED: 'APPROVED',
    DELETED: 'DELETED'
};

/**
 * TODO: Describe Review Types
 */
var REVIEW_TYPES = {
    IMAGE_PROCESSING: 'IMAGE_PROCESSING',
    DATA_QUALITY: 'DATA_QUALITY',
    EXPERT_REVIEW: 'EXPERT_REVIEW',
    VETTING: 'VETTING'
};

var REVIEW_SUBJECT_TYPES = {
    ITEM: 'ITEM',
    PUBLICATION: 'PUBLICATION'
};

var REVIEW_ACTION_NAMES = {
    ASSIGN_REVIEW: 'ASSIGN_REVIEW',
    APPROVE_REVIEW: 'APPROVED'
};

var REASON_CODES = {
    VOID: 'VOID',
    ADDITIONAL_IMAGES_REQUESTED: 'ADDITIONAL_IMAGES_REQUESTED',
    ADDITIONAL_DETAILS_REQUESTED: 'ADDITIONAL_DETAILS_REQUESTED',
    ATTRIBUTION_VERIFICATION_REQUESTED: 'ATTRIBUTION_VERIFICATION_REQUESTED',
    ENHANCED_IMAGES_REQUESTED: 'ENHANCED_IMAGES_REQUESTED',
    GENERAL_RESPONSE: 'GENERAL_RESPONSE',
    PERIOD_VERIFICATION_REQUESTED: 'PERIOD_VERIFICATION_REQUESTED',
    RESTRICTED_MATERIALS: 'RESTRICTED_MATERIALS'
};

// ---------------------------------------------------
// Dependencies

var Backbone = require('dibs-backbone');
var endpoints = require('dibs-endpoints');
var AvailableActions = require('bunsen/collections/AvailableActions');
var transitionMixin = require('bunsen/models/mixins/transitionMixin');
var Note = require('bunsen/models/Note');
var User = require('bunsen/models/identity/User');
var Permission = require('bunsen/models/identity/Permission');
var _ = require('underscore');

// ---------------------------------------------------
// Define Module Instance Properties

_.extend(Review.prototype, {
    relations: [
        {
            key : 'actions',
            type: Backbone.Many,
            collectionType: AvailableActions
        }
    ],

    url: function () {
        return endpoints.inventoryReview(this.get('id'));
    },

    /**
     * Gets the subjectEntity property from the review
     */
    getSubjectId: function () {
        return this.get('subject.sourceId');
    },
    /**
     * Gets the subjectEntity property from the review
     */
    getSubjectType: function () {
        return this.get('subject.sourceType');
    },

    /**
     * Sets the subject of the review to an anchor item, based on the ID passed in
     * @param {string} itemId
     */
    setAnchorItem: function (itemId) {
        assert(itemId, 'You must pass in an itemId to setAnchorItem');
        this.set('subject', {
            apiType: 'anchor',
            sourceId: itemId,
            sourceType: 'ITEM'
        });
    },
    /**
     * Sets the status of the review to Pending
     */
    markPending: function () {
        this.set('status', Review.STATUS.PENDING);
    },

    /**
     * Sets the status of the review to Approved
     */
    markApproved: function () {
        this.set('status', Review.STATUS.APPROVED);
    },

    /**
     * Sets the status of the review to Deleted
     */
    markDeleted: function () {
        this.set('status', Review.STATUS.DELETED);
    },

    /* Review Doc Statuses */
    /**
     * Returns true if the review is in New status
     */
    isStatusNew: function () {
        return this.get('status') === Review.STATUS.NEW;
    },

    /**
     * Returns true if the review is in Pending status
     */
    isStatusPending: function () {
        return this.get('status') === Review.STATUS.PENDING;
    },

    /**
     * Returns true if the review is in Approved status
     */
    isStatusApproved: function () {
        return this.get('status') === Review.STATUS.APPROVED;
    },

    /**
     * Returns true if the review is in Deleted status
     */
    isStatusDeleted: function () {
        return this.get('status') === Review.STATUS.DELETED;
    },

    /* Review Doc Types */
    /**
     * Returns true if the review is the Image Processing type
     */
    isTypeImageProcessing: function () {
        return this.get('type') === Review.TYPE.IMAGE_PROCESSING;
    },

    /**
     * Returns true if the review is the Data Quality type
     */
    isTypeDataQuality: function () {
        return this.get('type') === Review.TYPE.DATA_QUALITY;
    },

    /**
     * Returns true if the review is the Expert Review type
     */
    isTypeExpertReview: function () {
        return this.get('type') === Review.TYPE.EXPERT_REVIEW;
    },

    /**
     * Returns true if the review is the Vetting Review type
     */
    isTypeVettingReview: function () {
        return this.get('type') === Review.TYPE.VETTING;
    },

    isRejected: function () {
        return this.has('reasonCode') &&
            this.get('reasonCode') !== Review.REASON_CODES.VOID &&
            this.isTypeExpertReview() &&
            this.isStatusDeleted();
    },

    /**
     * Returns true if the review can be assigned and the user has permission to do so
     * @param {User} user
     * @param {string} type - The type of Review
     * @returns {boolean}
     */
    canAssignReview: function (user, type) {
        var actions;
        var assignReviewAction;
        var permissionNeeded;
        assert(user instanceof User, 'You must include a user when checking to see if a review can be assigned');
        assert(type, 'You must include the review type when checking if review can be assigned');

        actions = this.get('actions');
        assignReviewAction = actions && actions.findWhere({ code: Review.ACTION_NAMES.ASSIGN_REVIEW });
        if (type === Review.TYPE.IMAGE_PROCESSING) {
            permissionNeeded = Permission.PERMISSION_REVIEW_PHOTO_ASSIGN;
        } else if (type === Review.TYPE.DATA_QUALITY) {
            permissionNeeded = Permission.PERMISSION_REVIEW_DATA_ASSIGN;
        }

        return !!(assignReviewAction && permissionNeeded && user.hasPermission(permissionNeeded));
    },

    /**
     * Returns true if the review can be approved and the user has permission to do so
     * @param {User} user
     * @param {string} type - The type of Review
     * @returns {boolean}
     */
    canApproveReview: function (user, type) {
        var actions;
        var approveReviewAction;
        var permissionNeeded;
        assert(user instanceof User, 'You must include a user when checking to see if a review can be approved');
        assert(type, 'You must include the review type when checking if review can be approved');

        actions = this.get('actions');
        approveReviewAction = actions && actions.findWhere({ code: Review.ACTION_NAMES.APPROVE_REVIEW });
        if (type === Review.TYPE.IMAGE_PROCESSING) {
            permissionNeeded = Permission.PERMISSION_REVIEW_APPROVE_PHOTO_DOCUMENT;
        } else if (type === Review.TYPE.DATA_QUALITY) {
            permissionNeeded = Permission.PERMISSION_REVIEW_APPROVE_DATA_DOCUMENT;
        }

        return !!(approveReviewAction && permissionNeeded && user.hasPermission(permissionNeeded));
    },

    /**
     * Delete expert review - indicates rejection of an item
     * @param options - contains the associated item, and the details of the rejection
     */
    rejectExpertReview: function (options) {
        var subject;
        var notes;
        options = options || {};
        assert(options.itemId, 'You must include an associated item for this rejection.');
        assert(options.rejectReason, 'You must include a reason for this rejection.');
        assert(options.rejectMessage, 'You must include a message for this rejection.');
        assert(this.isTypeExpertReview(), 'Review must be type EXPERT_REVIEW to be rejected.');

        subject = {
            apiType : 'anchor',
            sourceId : options.itemId,
            sourceType : Review.SUBJECT_TYPE.ITEM
        };
        notes = [{
            "apiType": "note",
            "type": Note.TYPE_EXPERT_REVIEW_REJECTION,
            "content": options.rejectMessage
        }];

        this.markDeleted();
        this.set({
            reasonCode : options.rejectReason,
            subject : subject,
            notes : notes
        });

        return this.save(null, {queries : {
            indexMode : "sync"
        }});
    }
}, transitionMixin);

_.extend(Review, {
    STATUS: REVIEW_STATUSES,

    TYPE: REVIEW_TYPES,

    SUBJECT_TYPE: REVIEW_SUBJECT_TYPES,

    ACTION_NAMES: REVIEW_ACTION_NAMES,

    REASON_CODES: REASON_CODES
});



//////////////////
// WEBPACK FOOTER
// ./~/bunsen/models/inventory/Review.js
// module id = 2054
// module chunks = 63